import {
  AnvilSelectOptionsProps,
  Button,
  Checkbox,
  Form,
  Stack,
} from '@servicetitan/design-system';
import { FieldPath, useFormContext } from 'react-hook-form';
import {
  countrySelectOptions,
  getStatesOptionsForCountryCode,
} from 'src/constants/countryWithStates';
import strings from './strings';
import { useMemo, useState } from 'react';
import useAddressFormManagerContext from '../../context/useAddressFormManagerContext';
import AddressVerificationStatus from '../AddressVerificationStatus/AddressVerificationStatus';
import { CreateCustomerFormValues } from '@components/CreateCustomer/components/CreateCustomerForm/CreateCustomerFormDefinitions';
import {
  AddresFormPlaceholders,
  defaultAddress,
} from './AddressFormDefinitions';
import NumberInput from '@components/forms/components/NumberInput';
import { inboxLocators } from '@locators/inbox';

export interface AddressFormProps {
  hideAddressVerificationAndSearch?: boolean;
  placeholders?: AddresFormPlaceholders;
}

/**
 * Renders a form for entering address information. This form component can probably be reused in other forms.
 * @component
 */
const AddressForm = ({
  hideAddressVerificationAndSearch = false,
  placeholders,
}: AddressFormProps) => {
  const [stateSearchInput, setStateSearchInput] = useState('');
  const [isManualCoordinatesEntryChecked, setIsManualCoordinatesEntryChecked] =
    useState(false);
  const {
    trigger,
    register,
    getValues,
    setValue,
    formState: { errors },
  } = useFormContext<CreateCustomerFormValues>();
  const { formFieldPrefix, hideFullForm } = useAddressFormManagerContext();

  const { street, city, unit, state, country, zip, latitude, longitude } =
    getValues(`${formFieldPrefix}.address`) ?? defaultAddress;

  // sets the address.country and resets the state when the country is changed
  const handleCountryChange = (value: string) => {
    handleAddressFieldChange(`${formFieldPrefix}.address.country`, value);
    handleStateChange('');
    trigger(`${formFieldPrefix}.address.country`);
  };

  // sets the address.state field when the state is changed
  const handleStateChange = (value: string) => {
    handleAddressFieldChange(`${formFieldPrefix}.address.state`, value);
    trigger(`${formFieldPrefix}.address.state`);
    setStateSearchInput('');
  };

  const handleAddressFieldChange = (
    fieldName: FieldPath<CreateCustomerFormValues>,
    value: string,
  ) => {
    setValue(fieldName, value, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const handleGeoCoordinatesChange = (
    fieldName: FieldPath<CreateCustomerFormValues>,
    value: string,
  ) => {
    handleAddressFieldChange(fieldName, value);
    trigger(fieldName);
  };

  const stateOptions = useMemo(() => {
    const states = getStatesOptionsForCountryCode(country ?? 'US');
    if (!stateSearchInput) {
      return states;
    } else if (stateSearchInput.length === 1) {
      // if the search input is only one character, show all states that start with that character first and then show states that contain that character
      const startsWith: AnvilSelectOptionsProps[] = [];
      const contains: AnvilSelectOptionsProps[] = [];
      states.forEach((state) => {
        if (
          state.value.toLowerCase().startsWith(stateSearchInput.toLowerCase())
        ) {
          startsWith.push(state);
        } else if (
          state.value.toLowerCase().includes(stateSearchInput.toLowerCase())
        ) {
          contains.push(state);
        }
      });
      return [...startsWith, ...contains];
    } else {
      return states.filter((state) =>
        state.value.toLowerCase().startsWith(stateSearchInput.toLowerCase()),
      );
    }
  }, [country, stateSearchInput]);

  return (
    <>
      <Form.Group className='m-b-3'>
        <Form.Input
          required
          id={inboxLocators.customerSearch.createFormFields.address.street}
          label={strings.labels.street}
          placeholder={placeholders?.street ?? strings.placeholders.street}
          small
          width='7'
          value={street}
          error={errors[formFieldPrefix]?.address?.street?.message}
          {...register(`${formFieldPrefix}.address.street`)}
          onBlur={() => trigger(`${formFieldPrefix}.address.street`)}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            handleAddressFieldChange(
              `${formFieldPrefix}.address.street`,
              e.target.value,
            )
          }
        />
        <Form.Input
          label={strings.labels.unit}
          id={inboxLocators.customerSearch.createFormFields.address.unit}
          placeholder={placeholders?.unit ?? strings.placeholders.unit}
          small
          width='5'
          error={errors[formFieldPrefix]?.address?.unit?.message}
          value={unit}
          {...register(`${formFieldPrefix}.address.unit`)}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            handleAddressFieldChange(
              `${formFieldPrefix}.address.unit`,
              e.target.value,
            )
          }
        />
      </Form.Group>
      <Form.Group>
        <Form.Input
          required
          label={strings.labels.city}
          id={inboxLocators.customerSearch.createFormFields.address.city}
          placeholder={placeholders?.city ?? strings.placeholders.city}
          small
          width='7'
          error={errors[formFieldPrefix]?.address?.city?.message}
          value={city}
          {...register(`${formFieldPrefix}.address.city`)}
          onBlur={() => trigger(`${formFieldPrefix}.address.city`)}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            handleAddressFieldChange(
              `${formFieldPrefix}.address.city`,
              e.target.value,
            )
          }
        />
        <Form.AnvilSelect
          search={{
            placeholder: 'State',
            onChange: (e) => setStateSearchInput(e.target.value),
          }}
          required
          options={stateOptions}
          id={inboxLocators.customerSearch.createFormFields.address.state}
          label={strings.labels.state}
          width='5'
          trigger={{
            size: 'small',
            clearable: false,
            placeholder: placeholders?.state ?? strings.placeholders.state,
          }}
          error={errors[formFieldPrefix]?.address?.state?.message}
          value={state}
          {...register(`${formFieldPrefix}.address.state`)}
          onChange={(data) =>
            handleStateChange((data as AnvilSelectOptionsProps).value)
          }
          scrollHeight='30vh'
          autoFlipVertically={true}
        />
      </Form.Group>
      <Form.Group className='m-b-2'>
        <Form.AnvilSelect
          required
          options={countrySelectOptions}
          label={strings.labels.country}
          id={inboxLocators.customerSearch.createFormFields.address.country}
          width='7'
          trigger={{
            size: 'small',
            placeholder: placeholders?.country ?? strings.placeholders.country,
            clearable: false,
          }}
          error={errors[formFieldPrefix]?.address?.country?.message}
          value={country}
          {...register(`${formFieldPrefix}.address.country`)}
          onChange={(data) =>
            handleCountryChange((data as AnvilSelectOptionsProps).value)
          }
        />
        <Form.Input
          required
          label={strings.labels.zip}
          id={inboxLocators.customerSearch.createFormFields.address.zip}
          placeholder={placeholders?.zip ?? strings.placeholders.zip}
          small
          width='5'
          error={errors[formFieldPrefix]?.address?.zip?.message}
          value={zip}
          {...register(`${formFieldPrefix}.address.zip`)}
          onBlur={() => trigger(`${formFieldPrefix}.address.zip`)}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            handleAddressFieldChange(
              `${formFieldPrefix}.address.zip`,
              e.target.value,
            )
          }
        />
      </Form.Group>
      {!hideAddressVerificationAndSearch && (
        <>
          <Form.Group className='m-b-3'>
            <Stack
              className='p-x-2 w-100'
              justifyContent='space-between'
              alignItems='center'
            >
              <AddressVerificationStatus />
              <Button primary fill='subtle' size='small' onClick={hideFullForm}>
                {strings.labels.addressSearchButton}
              </Button>
            </Stack>
          </Form.Group>
          <Checkbox
            className='m-b-3'
            label={strings.labels.manualCoordinates}
            checked={isManualCoordinatesEntryChecked}
            onChange={() =>
              setIsManualCoordinatesEntryChecked(
                !isManualCoordinatesEntryChecked,
              )
            }
          />
          {isManualCoordinatesEntryChecked && (
            <Form.Group>
              <NumberInput
                label={strings.labels.latitude}
                placeholder={strings.placeholders.latitude}
                small
                width={6}
                value={latitude}
                error={errors[formFieldPrefix]?.address?.latitude?.message}
                {...register(`${formFieldPrefix}.address.latitude`)}
                onBlur={() => trigger(`${formFieldPrefix}.address.latitude`)}
                onChange={(_e, data) => {
                  handleGeoCoordinatesChange(
                    `${formFieldPrefix}.address.latitude`,
                    data?.value,
                  );
                }}
              />
              <NumberInput
                label={strings.labels.longitude}
                placeholder={strings.placeholders.longitude}
                small
                width={6}
                error={errors[formFieldPrefix]?.address?.longitude?.message}
                value={longitude}
                {...register(`${formFieldPrefix}.address.longitude`)}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleGeoCoordinatesChange(
                    `${formFieldPrefix}.address.longitude`,
                    e.target.value,
                  )
                }
              />
            </Form.Group>
          )}
        </>
      )}
    </>
  );
};
export default AddressForm;
